import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader, QrCode } from 'lucide-react';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import metamaskIcon from '@/assets/images/metamask.svg';
import phantomIcon from '@/assets/images/phantom.svg';

/**
 * Modal para conectar carteiras (Metamask e Phantom)
 * 
 * ⚠️  NOTA IMPORTANTE:
 * Se você tiver múltiplas extensões de carteira instaladas (Metamask + Uniswap, etc),
 * e Metamask não funcionar (abrindo outra carteira),
 * você pode desabilitar o botão Metamask mudando:
 * const ENABLE_METAMASK = false; (linha abaixo)
 * 
 * Phantom é mais confiável para este cenário.
 */

// 🔧 CONFIGURAÇÃO: Desabilitar Metamask se necessário
const ENABLE_METAMASK = true; // Mude para 'false' para remover opção Metamask

export default function WalletConnectionModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const {
    connectedWallet,
    isConnecting,
    error,
    isPhantomAvailable,
    connectMetamask,
    connectPhantom,
    isMobile,
    openInMetaMaskApp,
    connectWalletConnect,
  } = useWalletConnection();

  const handleMetamaskDownloadClick = (e) => {
    if (isMobile) {
      // Em mobile, em vez de abrir a página genérica de download,
      // abrimos diretamente o app/navegador da própria MetaMask apontando para o dapp.
      e.preventDefault();
      try {
        openInMetaMaskApp();
      } catch {
        // fallback silencioso: se falhar, o usuário ainda pode instalar manualmente
        window.location.href = 'https://metamask.io/download/';
      }
    }
  };

  if (!isOpen) return null;

  const handleMetamaskClick = async () => {
    try {
      await connectMetamask();
      onClose();
    } catch {
      // Erro já tratado pelo contexto (toast)
    }
  };

  const handlePhantomClick = async () => {
    try {
      await connectPhantom();
      onClose();
    } catch {
      // Erro já tratado pelo contexto (toast)
    }
  };

  const handleWalletConnectClick = async () => {
    try {
      await connectWalletConnect();
      onClose();
    } catch {
      // Erro já tratado pelo contexto (toast)
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-lg border border-purple-500/30 shadow-2xl max-w-md w-full animate-in fade-in zoom-in-95">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-purple-500/20">
            <h2 className="text-xl font-bold text-white">{t('wallets.connectWallet')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition"
            >
              <X size={24} />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6 space-y-4">
            {/* Mensagem de erro */}
            {error && (
              <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Opção Metamask (desktop) - no mobile deixamos apenas WalletConnect gerenciar EVM */}
            {ENABLE_METAMASK && !isMobile && (
              <>
                <button
                  onClick={handleMetamaskClick}
                  disabled={isConnecting || connectedWallet === 'metamask'}
                  className={`w-full p-4 rounded-lg border-2 transition flex items-center gap-3 ${
                    connectedWallet === 'metamask'
                      ? 'border-green-500 bg-green-900/20'
                      : 'border-orange-500/50 hover:border-orange-500 hover:bg-orange-900/10'
                  }`}
                >
                  <img
                    src={metamaskIcon}
                    alt="Metamask"
                    className="w-8 h-8"
                  />
                  <div className="text-left flex-1">
                    <div className="font-semibold text-white">
                      {connectedWallet === 'metamask' ? t('wallets.connected') : t('wallets.metamask')}
                    </div>
                    <div className="text-xs text-gray-400">
                      {isMobile ? t('wallets.evmMobileHint') : t('wallets.evmNetworks')}
                    </div>
                  </div>
                  {isConnecting && <Loader size={20} className="animate-spin text-orange-500" />}
                </button>
                {isMobile && connectedWallet !== 'metamask' && (
                  <p className="text-xs text-gray-400 -mt-2">
                    {t('wallets.mobileOpensApp')}
                  </p>
                )}
              </>
            )}

            {/* Opção WalletConnect (multi-wallet, ideal mobile) - apenas em mobile */}
            {isMobile && (
              <button
                onClick={handleWalletConnectClick}
                disabled={isConnecting || connectedWallet === 'walletconnect'}
                className={`w-full p-4 rounded-lg border-2 transition flex items-center gap-3 border-purple-500/60 hover:border-purple-400 hover:bg-purple-900/20 ${
                  connectedWallet === 'walletconnect' ? 'border-green-500 bg-green-900/20' : ''
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-purple-600/30 flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-purple-200" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-semibold text-white">
                    {connectedWallet === 'walletconnect' ? t('wallets.walletConnectConnected') : t('wallets.walletConnect')}
                  </div>
                  <div className="text-xs text-gray-400">
                    {t('wallets.walletConnectHint')}
                  </div>
                </div>
                {isConnecting && <Loader size={20} className="animate-spin text-purple-400" />}
              </button>
            )}

            {/* Opção Phantom — oculta no mobile (temporariamente) */}
            {!isMobile && (
              <>
                <button
                  onClick={handlePhantomClick}
                  disabled={!isPhantomAvailable() || isConnecting || connectedWallet === 'phantom'}
                  className={`w-full p-4 rounded-lg border-2 transition flex items-center gap-3 ${
                    isPhantomAvailable()
                      ? 'border-blue-500/50 hover:border-blue-500 hover:bg-blue-900/10'
                      : 'border-gray-600/50 opacity-50 cursor-not-allowed'
                  } ${connectedWallet === 'phantom' ? 'border-green-500 bg-green-900/20' : ''}`}
                >
                  <img
                    src={phantomIcon}
                    alt="Phantom"
                    className="w-8 h-8"
                  />
                  <div className="text-left flex-1">
                    <div className="font-semibold text-white">
                      {connectedWallet === 'phantom' ? t('wallets.connected') : t('wallets.phantom')}
                    </div>
                    <div className="text-xs text-gray-400">
                      {isPhantomAvailable() ? t('wallets.solanaNetwork') : t('wallets.notInstalled')}
                    </div>
                  </div>
                  {isConnecting && <Loader size={20} className="animate-spin text-blue-500" />}
                </button>
                {isPhantomAvailable() && (
                  <p className="text-[11px] text-gray-500 -mt-1 px-1">
                    {t('wallets.phantomDesktopHint')}
                  </p>
                )}
              </>
            )}

            {/* Dica */}
            <div className="mt-6 pt-4 border-t border-gray-700/50">
              <p className="text-xs text-gray-400 mb-3">
                {t('wallets.tipTitle')}
              </p>
              <div className="space-y-2">
                <a
                  href="https://metamask.io/download/"
                  onClick={handleMetamaskDownloadClick}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-blue-400 hover:text-blue-300 transition"
                >
                  {t('wallets.downloadMetamask')}
                </a>
                {!isMobile && (
                  <a
                    href="https://phantom.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-xs text-blue-400 hover:text-blue-300 transition"
                  >
                    {t('wallets.downloadPhantom')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
